import { message } from "antd";
import { type InjectedFormikProps } from "formik";
import { Component } from "react";

import { showAPIErrorMessage } from "utilities/apiHandler";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTFormItem, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { PayrollEmployeeSyncStatusDisplay } from "entity/internalUser/InternalUserDetails/common/PayrollEmployeeSyncStatusDisplay";
import { type IInternalUserHandler } from "entity/internalUser/InternalUserDetails/InternalUser.api.handler";
import {
    type IInternalUserFormValues,
    type InternalUsersPayrollSettingsResponse,
    type PayrollTabFormActions,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";

import "./InternalUserPayrollTab.less";

interface IInternalUserPayrollTabProps {
    id: number;
    firstName: string;
    entity: InternalUsersPayrollSettingsResponse;
    onReload: () => Promise<void>;
    handler?: IInternalUserHandler;
}

class InternalUserPayrollTabState {
    actionBeingPerformed: PayrollTabFormActions;
    failedToSync: boolean;
    errorTitle: string | undefined;
    errorDescription: string | undefined;
    loading: boolean;
}

export class InternalUserPayrollTab extends Component<
    InjectedFormikProps<IInternalUserPayrollTabProps, IInternalUserFormValues>
> {
    state: InternalUserPayrollTabState = {
        actionBeingPerformed: undefined,
        failedToSync: false,
        errorTitle: undefined,
        errorDescription: undefined,
        loading: false,
    };

    componentDidUpdate(nextProps: IInternalUserPayrollTabProps) {
        const { entity } = this.props;

        // i had to do this so that error messages wont persist after a refresh...
        if (nextProps.entity !== entity) {
            this.setState({
                failedToSync: false,
                errorTitle: undefined,
                errorDescription: undefined,
            });
        }
    }

    private handleAddToGusto = async () => {
        try {
            const { id, firstName, onReload } = this.props;

            this.setState({ loading: true });
            const response = await this.props.handler!.addToGusto(id);

            if (response?.errorList) {
                const error = response.errorList[0];

                this.setState({
                    failedToSync: true,
                    errorTitle: "There was a problem adding " + firstName + " to gusto.",
                    errorDescription: error,
                });
            } else {
                void message.success("Succesfully added " + firstName + " to gusto.");
                this.setState({
                    failedToSync: false,
                    errorTitle: undefined,
                    errorDescription: undefined,
                });
                await onReload();
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }

        this.setState({ loading: false });
    };

    private handleSyncWithGusto = async () => {
        try {
            const { id, firstName, onReload } = this.props;

            this.setState({ loading: true });

            const response = await this.props.handler!.syncWithGusto(id);

            if (response?.errorList) {
                const error = response.errorList[0];

                this.setState({
                    failedToSync: true,
                    errorTitle: firstName + " could not be synced",
                    errorDescription:
                        error +
                        ` You can sync manually by choosing an employee from the menu below and clicking "Save", or click "Create User in Gusto" to generate a new Gusto employee to sync with.`,
                });
            } else {
                this.setState({
                    failedToSync: false,
                    errorTitle: undefined,
                    errorDescription: undefined,
                });
                void message.success("Succesfully synced " + firstName + " with gusto.");
                await onReload();
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }

        this.setState({ loading: false });
    };

    private handleDisconnectFromGusto = async () => {
        try {
            const { id, firstName, onReload } = this.props;
            this.setState({ loading: true });
            const response = await this.props.handler!.disconnectFromGusto(id);

            if (response?.errorList) {
                this.setState({
                    failedToSync: true,
                    errorDescription:
                        "There was a problem disconnecting " + firstName + " from gusto.",
                });
            } else {
                await onReload();
                void message.success("Succesfully disconnected " + firstName + " from gusto.");
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }

        this.setState({ loading: false });
    };

    private JobDropDown = () => {
        const { entity, setFieldValue, setFieldTouched, values } = this.props;
        const { payrollIntegratioName, availableJobs, canUpdate } = entity;

        return availableJobs ? (
            <BTFormItemAutomatic<IInternalUserFormValues>
                id="payrollJobID"
                label={payrollIntegratioName + " Job"}
            >
                <BTSelect<IInternalUserFormValues>
                    id="payrollJobID"
                    data-testid="payrollJobID"
                    value={values.payrollJobID}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    treeData={availableJobs}
                    disabled={!canUpdate}
                    className="margin-bottom-sm"
                />
            </BTFormItemAutomatic>
        ) : (
            <></>
        );
    };

    private EmployeeDropDown = () => {
        const { entity, setFieldValue, setFieldTouched, values } = this.props;
        const { payrollIntegratioName, payrollEmployees, canUpdate } = entity;

        return (
            <BTFormItemAutomatic<IInternalUserFormValues>
                id="payrollEmployeeId"
                label={"Linked " + payrollIntegratioName + " Employee"}
            >
                <BTSelect<IInternalUserFormValues>
                    id="payrollEmployeeId"
                    data-testid="payrollEmployeeId"
                    value={values.payrollEmployeeId}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    treeData={payrollEmployees}
                    disabled={!canUpdate}
                    className="margin-bottom-sm"
                />
            </BTFormItemAutomatic>
        );
    };

    private AddButton = () => {
        const { entity } = this.props;
        const { payrollIntegratioName } = entity;

        return (
            <BTButtonDeprecated<PayrollTabFormActions>
                data-testid="add-to-payroll-provider"
                onClick={this.handleAddToGusto}
                actionBeingPerformed={this.state.actionBeingPerformed}
                loadingAction="add"
            >
                Create User in {payrollIntegratioName}
            </BTButtonDeprecated>
        );
    };

    private SyncButton = () => {
        const { entity } = this.props;
        const { payrollIntegratioName } = entity;

        return (
            <BTPopover
                content={`Attempt to link this user to and existing employee in ${payrollIntegratioName}.`}
                placement="top"
            >
                <BTButtonDeprecated<PayrollTabFormActions>
                    data-testid="sync-with-payroll-provider"
                    onClick={this.handleSyncWithGusto}
                    actionBeingPerformed={this.state.actionBeingPerformed}
                    loadingAction="sync"
                    className="margin-right-sm"
                >
                    Sync With {payrollIntegratioName} User
                </BTButtonDeprecated>
            </BTPopover>
        );
    };

    render() {
        const { entity } = this.props;
        const { failedToSync, errorDescription, errorTitle, loading } = this.state;
        const { payrollLogoUrl, payrollIntegratioName, payrollEmployeeId } = entity;

        const DisconnectButton = () => {
            return (
                <BTButtonDeprecated<PayrollTabFormActions>
                    data-testid="disconnect-from-payroll-provider"
                    onClick={this.handleDisconnectFromGusto}
                    actionBeingPerformed={this.state.actionBeingPerformed}
                    loadingAction="sync"
                    className="margin-right-sm"
                    type="destruction"
                >
                    Disconnect
                </BTButtonDeprecated>
            );
        };

        return (
            <>
                {loading && <BTLoading />}

                <PageSection title="Payroll">
                    <BTRow className="justify-content-start align-items-baseline" gutter={16}>
                        <BTCol span={8}>
                            <img
                                className="Internal-User-Details-Payroll-Logo margin-bottom-lg"
                                src={payrollLogoUrl}
                                alt={payrollIntegratioName}
                            />
                            <div className="margin-bottom-lg">
                                <PayrollEmployeeSyncStatusDisplay
                                    status={entity.syncStatus}
                                    payrollIntegrationName={payrollIntegratioName}
                                />
                            </div>
                            {payrollEmployeeId !== "-1" && (
                                <>
                                    {this.EmployeeDropDown()}
                                    {this.JobDropDown()}
                                    {DisconnectButton()}
                                </>
                            )}
                        </BTCol>
                    </BTRow>

                    {payrollEmployeeId === "-1" && !failedToSync && (
                        <BTRow className="justify-content-start align-items-baseline" gutter={16}>
                            <BTCol span={16}>
                                {this.SyncButton()}
                                {this.AddButton()}
                            </BTCol>
                        </BTRow>
                    )}
                    {errorTitle && errorDescription && (
                        <BTRow className="justify-content-start align-items-baseline" gutter={16}>
                            <BTCol span={16}>
                                <BTAlert
                                    title={errorTitle}
                                    message={errorDescription}
                                    showIcon={true}
                                    type="error"
                                    className="margin-bottom-lg"
                                    data-testid="payrollError"
                                />
                            </BTCol>
                        </BTRow>
                    )}
                    {payrollEmployeeId === "-1" && failedToSync && (
                        <BTRow className="justify-content-start align-items-baseline" gutter={16}>
                            <BTCol span={8}>{this.EmployeeDropDown()}</BTCol>
                            <BTCol span={8} className="padding-left-sm">
                                <BTFormItem label={<>&nbsp;</>}>
                                    <span className="margin-right-sm"> - or - </span>
                                    {this.AddButton()}
                                </BTFormItem>
                            </BTCol>
                        </BTRow>
                    )}
                </PageSection>
            </>
        );
    }
}
